import { z } from 'zod'

// Analytical-tags rules engine — shared shapes
// (spec/plans/2026-08-14-etiquetas-analiticas.md §6).
//
// The DB rows live in tag_rules / tag_rule_conditions / tag_rule_allocations /
// rule_fields; these schemas are the wire/validation shapes shared by the web
// rule editor (client-side validation before f_save_tag_rule) and the
// evaluator (tag-rules.ts). Mirrored into the poller by hand (ai.ts discipline).

export const TagRuleOperator = z.enum([
  // text
  'contains',
  'starts_with',
  'ends_with',
  // enum / text equality
  'eq',
  'neq',
  'in',
  // number
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  // date
  'on',
  'before',
  'after',
])
export type TagRuleOperator = z.infer<typeof TagRuleOperator>

export const RuleFieldRow = z.object({
  key: z.string().min(1),
  label_pt: z.string().min(1),
  data_type: z.enum(['text', 'number', 'date', 'enum']),
  allowed_operators: z.array(z.string()),
  source: z.enum(['extraction', 'ingestion', 'reference']),
  min_confidence: z.number().min(0).max(1).nullable(),
  active: z.boolean(),
  sort_order: z.number().int(),
})
export type RuleFieldRow = z.infer<typeof RuleFieldRow>

export const TagRuleCondition = z.object({
  field_key: z.string().min(1),
  operator: TagRuleOperator,
  // jsonb: string | number | array of either, depending on the operator.
  value: z.union([z.string(), z.number(), z.array(z.union([z.string(), z.number()]))]),
  value_secondary: z.union([z.string(), z.number()]).nullable().optional(),
})
export type TagRuleCondition = z.infer<typeof TagRuleCondition>

export const TagRuleAllocation = z.object({
  tag_id: z.string().uuid(),
  percentage: z.number().gt(0).max(100),
  // Joined from client_tags at load time — the evaluator groups by dimension.
  dimension: z.string().min(1),
})
export type TagRuleAllocation = z.infer<typeof TagRuleAllocation>

export const EvalTagRule = z.object({
  id: z.string().uuid(),
  client_id: z.string().uuid(),
  name: z.string().min(1),
  priority: z.number().int().nullable(),
  active: z.boolean(),
  created_at: z.string(),
  conditions: z.array(TagRuleCondition).min(1),
  allocations: z.array(TagRuleAllocation).min(1),
})
export type EvalTagRule = z.infer<typeof EvalTagRule>

// The f_save_tag_rule payload (client-side pre-validation mirrors the RPC's
// checks so the editor can show errors without a round-trip).
export const TagRuleInput = z.object({
  id: z.string().uuid().optional(),
  client_id: z.string().uuid(),
  name: z.string().min(1),
  priority: z.number().int().nullable().optional(),
  active: z.boolean().optional(),
  conditions: z
    .array(
      TagRuleCondition.pick({ field_key: true, operator: true, value: true }).extend({
        value_secondary: TagRuleCondition.shape.value_secondary,
      }),
    )
    .min(1),
  allocations: z
    .array(z.object({ tag_id: z.string().uuid(), percentage: z.number().gt(0).max(100) }))
    .min(1),
})
export type TagRuleInput = z.infer<typeof TagRuleInput>
